import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ViewEncapsulation,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { DialogService } from 'src/app/core/services/dialog/dialog.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { SelectorData } from 'src/app/utils/selectorDataType';
import { MatOptionSelectionChange } from '@angular/material/core';

@Component({
  selector: 'app-multi-select',
  templateUrl: './multi-select.component.html',
  styleUrls: ['./multi-select.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
/**
 * Represents a generic, selector + multiselector field
 */
export class MultiSelectComponent implements OnInit {
  @Input() options: SelectorData[] = [];
  @Input() initialMultiData?: SelectorData[] = [];
  @Input() initialData?: SelectorData = { label: '', value: undefined };
  @Input() placeholder: string = 'Select an option';
  @Input() isMultiselector: boolean = false;
  @Input() disabled: boolean = false;
  @Input() entity?: string = 'options';
  @ViewChild('mySelect') mySelect!: MatSelect;

  @Input() showSelectAll = false;
  @Input() selectAllLabel = 'Select all';

  formControl = new FormControl();

  allSelected = false;
  readonly SELECT_ALL = '__SELECT_ALL__';
  isSelectOpen = false;
  originalValues?: SelectorData[] = [];
  labels = {
    regionChange: 'region-change',
    contentRegionCheckboxesEmpty: 'region-checkboxes-empty-1',
    secondaryRegionCheckboxesEmpty: 'region-checkboxes-empty-2',
    ok: 'ok',
  };

  @Output() valueChange = new EventEmitter<any>();
  @Output() closedWithChanges = new EventEmitter<any>();

  /**
   * Calls to initiate fields within the view
   */
  ngOnInit(): void {
    this.setInitialValues();
    this.syncAllSelected();
    this.translationService.translateObject(this.labels);
  }

  /**
   * Constructor
   */
  constructor(
    private readonly dialogService: DialogService,
    private translationService: TranslationService
  ) {}

  /**
   * isIndeterminate returns boolean with the selected values
   */
  get isIndeterminate(): boolean {
    const sel = this.formControl.value ?? [];
    return sel.length > 0 && sel.length < this.options.length;
  }

  /**
   * selectAllChecked returns boolean based on all selected values
   */
  get selectAllChecked(): boolean {
    const sel = Array.isArray(this.formControl.value)
      ? this.formControl.value
      : [];
    return (
      this.isMultiselector &&
      this.options?.length > 0 &&
      sel.length === this.options.length
    );
  }

  /**
   *
   * @param {MatOptionSelectionChange} e Selection change event.
   */
  onSelectAllOptionChange(e: MatOptionSelectionChange): void {
    if (!e.isUserInput) return;
    if (e.source.value !== this.SELECT_ALL) return;

    const next = !this.selectAllChecked;
    this.toggleAllSelection(next);
    e.source.deselect();
  }

  /**
   * Toggles all options based on the checkbox state.
   * @param {boolean} checked - If true selects all; otherwise clears.
   * @return {void}
   */
  toggleAllSelection(checked: boolean): void {
    let values: any[] = [];

    if (checked) {
      const opts = this.options ?? [];
      values = opts.map((o) => this.getOptionValue(o)).filter((v) => v != null);
    }

    this.formControl.setValue(values);
    this.syncAllSelected();
    this.valueChange.emit(values);
  }

  /**
   * Extracts the value from a SelectorData or returns the primitive itself.
   * @param {SelectorData|*} opt - Option object or a raw value.
   * @return {*} The extracted value (opt.value if present, otherwise opt).
   * @private
   */
  private getOptionValue(opt: SelectorData | unknown): unknown {
    return (opt as any)?.value ?? opt;
  }

  /**
   *
   * @private syncAllSelected method helps to sync of all the values are selected or any change in selection
   */
  private syncAllSelected(): void {
    const sel: any[] = Array.isArray(this.formControl.value)
      ? this.formControl.value
      : [];
    const total = this.options.length;
    this.allSelected = total > 0 && sel.length === total;
  }

  /**
   * Sets the initial values for both versions of selector field
   */
  setInitialValues() {
    if (this.isMultiselector) {
      this.formControl.setValue(
        this.initialMultiData?.map((data) => data.value) ?? []
      );
      this.originalValues =
        this.initialMultiData?.map((data) => data.value) ?? [];
    } else {
      this.formControl.setValue(this.initialData?.value);
    }
  }

  /**
   * Update the selected values
   * @param {SelectorData[]} selectedMultiData
   */
  updateSelectedValues(selectedMultiData?: SelectorData[]) {
    const vals = selectedMultiData?.map((d) => d.value) ?? [];
    this.formControl.setValue(vals);
    this.originalValues = [...vals];
    this.syncAllSelected();
  }

  /**
   * Gets the view name for a given profile role
   * @return {string}
   */
  getMultiSelectorPlaceholder(): string {
    const selected = this.formControl.value;
    const count = selected?.length || 0;
    if (count === this.options.length) return `All ${this.entity}`;
    else if (count === 1) {
      return this.options.find((option) => option.value === selected[0])
        ?.label!;
    }
    return count > 1 ? `${count} selected` : 'Select an option below';
  }

  /**
   *Defines the color of the placeholder given the amount of options selected
   * @return {boolean}
   */
  isBluePlaceholder(): boolean {
    const selected = this.formControl.value;
    const count = selected?.length || 0;
    return count > 1 && count !== this.options.length;
  }

  /**
   *  Propagates the change in value occurred in the selector
   * @param {MatSelectChange} event
   */
  onSelectionChange(event: MatSelectChange): void {
    this.syncAllSelected();
    this.valueChange.emit(event);
  }

  /**
   * Call to reset the value to the previous value, rollback.
   * @param {any[]} values
   */
  resetMultiSelectValue(values: any[]): void {
    this.formControl.setValue(values);
    this.originalValues = [...values];
  }

  /**
   * Call to reset the value to the previous value, rollback.
   * @param {any} value
   */
  resetSelectValue(value: any): void {
    this.formControl.setValue(value);
    this.originalValues = value;
  }

  /**
   * Reacts to opening and closing the selector
   */
  onClosed(): void {
    const raw = this.formControl.value;
    const rawArray = Array.isArray(raw) ? raw : [raw];
    const currentValues: any[] = rawArray.filter(
      (v) => v !== null && v !== undefined
    );

    const hasChanged =
      JSON.stringify(currentValues) !== JSON.stringify(this.originalValues);

    if (hasChanged) {
      this.closedWithChanges.emit(currentValues);
      this.originalValues = [...currentValues];
    }

    (document.activeElement as HTMLElement)?.blur();
  }
}
